import { availabilitiesAtom, itemModalStepAtom, itemOptionsQuantitiesAtom, itemsAtom, mandatoryOptionGroupsAtom, optionSpecialInstructionsAtom } from "@/atoms"
import { Button, FixedFooter, Price, QuantityStepper, Text } from "@/components/ui"
import { getSuggestionEventsSink } from "@/domain/suggestions"
import { useOptimisticCart } from "@/hooks/use-optimistic-cart"
import { useStoreClosedGuard } from "@/hooks/use-store-closed-guard"
import { getMerchantId } from "@/lib/company-storage"
import { getPortionTarget, isPortionOptionGroup, resolveEffectivePortionQuantities } from "@/lib/option-group-portions"
import { FLY_ORIGIN_ATTR, FLY_ORIGIN_ITEM_DETAIL, flyToCart } from "@/lib/fly-to-cart"
import { minuteNowAtom, tenantTimezoneAtom } from "@/atoms/tenant-clock"
import { isOfferAvailableNow } from "@/lib/is-offer-available-now"
import { computeItemTotal } from "@/lib/pricing"
import { consumeSuggestionNavigation } from "@/lib/suggestion-navigation"
import { CartItem, Category, Item, ItemOffer, ItemOptionsQuantities, OptionGroup } from "@/types"
import { atom, useAtom, useAtomValue } from "jotai"
import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { v4 as uuid } from "uuid"

const hasUnfilledMandatoryGroupAtom = atom(get =>
  Object.values(get(mandatoryOptionGroupsAtom)).includes(false)
)

/**
 * Quantity stepper + "Adicionar" for the item modal. Adding is refused for an
 * offer the checkout would refuse (`isOfferAvailableNow`): the menu cards hide
 * that path, but a deep link (`?item=`) or a stale tab opens the modal directly.
 * "Voltar" only appears on a step-by-step offer past its first step.
 */
export default function BottomButtons({
  item,
  itemOffer,
  optionGroups,
  specialInstructions,
  category = null,
}: {
  item: Item,
  itemOffer: ItemOffer,
  optionGroups: OptionGroup[],
  specialInstructions: string
  /** Owning category, when the caller resolved it; enforces category-level status and hours. */
  category?: Pick<Category, "status" | "availabilityId"> | null
}) {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useAtom(itemModalStepAtom)
  const items = useAtomValue(itemsAtom)
  const availabilities = useAtomValue(availabilitiesAtom)
  const timeZone = useAtomValue(tenantTimezoneAtom)
  const now = useAtomValue(minuteNowAtom)
  const isOrderable = isOfferAvailableNow(itemOffer, availabilities, category, { now, timeZone })
  const hasUnfilledMandatoryGroup = useAtomValue(hasUnfilledMandatoryGroupAtom)
  const itemOptionsQuantities = useAtomValue(itemOptionsQuantitiesAtom)
  const optionSpecialInstructions = useAtomValue(optionSpecialInstructionsAtom)

  const itemTotalPrice = useMemo(
    () => computeItemTotal(itemOffer, optionGroups, itemOptionsQuantities),
    [itemOffer, optionGroups, itemOptionsQuantities],
  )

  const { cart, addItem, updateItemQuantity } = useOptimisticCart()
  const { ensureStoreOpen } = useStoreClosedGuard()

  const [quantity, setQuantity] = useState(1)

  function increaseQuantity() { setQuantity(quantity + 1) }
  function decreaseQuantity() { setQuantity(quantity - 1) }

  function handleSubmit() {
    if (!isOrderable) return
    if (!ensureStoreOpen()) return

    const suggestionNav = consumeSuggestionNavigation(itemOffer.id)

    flyToCart(document.querySelector(`[${FLY_ORIGIN_ATTR}="${FLY_ORIGIN_ITEM_DETAIL}"]`))

    const optionGroupsInUse: Record<string, ItemOptionsQuantities> = Object.entries(itemOptionsQuantities).reduce((f_acc, [fKey, fValue]) => {
      const currentOptionGroup = optionGroups.find(opg => opg.id === fKey)

      const normalizedGroupQty = currentOptionGroup && isPortionOptionGroup(currentOptionGroup)
        ? resolveEffectivePortionQuantities(
          fValue,
          getPortionTarget(currentOptionGroup) ?? currentOptionGroup.maxPermitted,
        )
        : fValue

      const obj: ItemOptionsQuantities = Object.entries(normalizedGroupQty).reduce((s_acc, [sKey, sValue]) => sValue > 0 ? { ...s_acc, [sKey]: sValue } : s_acc, {})
      return Object.keys(obj).length ? { ...f_acc, [fKey]: obj } : f_acc
    }, {})

    const sortedOptionGroupsInUse = Object.fromEntries(
      Object.entries(optionGroupsInUse).sort(([a], [b]) => {
        const indexA = optionGroups.findIndex(og => og.id === a)
        const indexB = optionGroups.findIndex(og => og.id === b)
        return indexA - indexB
      })
    ) as Record<string, ItemOptionsQuantities>

    const cleanedOptions = optionGroups.filter(opg => Object.keys(sortedOptionGroupsInUse).includes(opg.id))

    const itemOptions: Record<string, Item> = Object
      .values(sortedOptionGroupsInUse)
      .map(optionQuantities => Object.keys(optionQuantities))
      .flat()
      .reduce((acc, optionId) => {
        const option = optionGroups.flatMap(og => og.options).find(opt => opt.id === optionId)
        if (option) {
          const item = items.find(i => i.id === option.itemId)
          if (item) {
            acc[optionId] = item
          }
        }
        return acc
      }, {} as Record<string, Item>)

    const lineTotalPrice = itemTotalPrice * quantity
    const lineUnitPrice = itemTotalPrice

    const filteredOptionSpecialInstructions = Object.entries(sortedOptionGroupsInUse).reduce<Record<string, Record<string, string>>>(
      (acc, [groupId, quantities]) => {
        const groupNotes = optionSpecialInstructions[groupId]
        if (!groupNotes) return acc
        const kept: Record<string, string> = {}
        Object.keys(quantities).forEach((optionId) => {
          const note = groupNotes[optionId]
          if (note && note.trim().length > 0) kept[optionId] = note
        })
        if (Object.keys(kept).length > 0) acc[groupId] = kept
        return acc
      },
      {}
    )

    const newItem: CartItem = {
      id: uuid(),
      item: item,
      itemOffer: itemOffer,
      itemOptions: itemOptions,
      optionGroups: cleanedOptions,
      optionQuantities: sortedOptionGroupsInUse,
      quantity: quantity,
      itemTotalPrice: lineTotalPrice,
      unitPrice: lineUnitPrice,
      specialInstructions: specialInstructions,
      optionSpecialInstructions: Object.keys(filteredOptionSpecialInstructions).length > 0
        ? filteredOptionSpecialInstructions
        : undefined,
      addedVia: suggestionNav?.surface ?? undefined,
      addedViaArm: suggestionNav?.arm ?? undefined,
      addedViaAnchorItemId: suggestionNav?.anchorItemId ?? undefined,
    }

    const existingItem = cart.items.find((existing) => {
      if (existing.itemOffer.id !== newItem.itemOffer.id) return false
      if ((existing.specialInstructions || '') !== (newItem.specialInstructions || '')) return false
      const existingKeys = Object.keys(existing.optionQuantities)
      const newKeys = Object.keys(newItem.optionQuantities)
      if (existingKeys.length !== newKeys.length) return false
      const sameQuantities = existingKeys.every((groupId) => {
        const existingGroup = existing.optionQuantities[groupId]
        const newGroup = newItem.optionQuantities[groupId]
        if (!newGroup) return false
        const existingOptKeys = Object.keys(existingGroup)
        const newOptKeys = Object.keys(newGroup)
        if (existingOptKeys.length !== newOptKeys.length) return false
        return existingOptKeys.every((optId) => existingGroup[optId] === newGroup[optId])
      })
      if (!sameQuantities) return false
      const existingNotes = existing.optionSpecialInstructions ?? {}
      const newNotes = newItem.optionSpecialInstructions ?? {}
      const existingNotesKeys = Object.keys(existingNotes)
      const newNotesKeys = Object.keys(newNotes)
      if (existingNotesKeys.length !== newNotesKeys.length) return false
      return existingNotesKeys.every((groupId) => {
        const existingGroup = existingNotes[groupId]
        const newGroup = newNotes[groupId]
        if (!newGroup) return false
        const existingOptKeys = Object.keys(existingGroup)
        const newOptKeys = Object.keys(newGroup)
        if (existingOptKeys.length !== newOptKeys.length) return false
        return existingOptKeys.every((optId) => existingGroup[optId] === newGroup[optId])
      })
    })

    if (existingItem) {
      const mergedQuantity = existingItem.quantity + newItem.quantity
      updateItemQuantity(existingItem.id, mergedQuantity)
    } else {
      addItem(newItem)
    }

    if (suggestionNav) {
      const merchantId = getMerchantId()
      if (merchantId) {
        getSuggestionEventsSink(merchantId).enqueue({
          type: "ADD",
          surface: suggestionNav.surface,
          itemIds: [item.id],
          arm: suggestionNav.arm,
          anchorItemId: suggestionNav.anchorItemId,
          items: [{ itemId: item.id, arm: suggestionNav.arm, anchorItemId: suggestionNav.anchorItemId }],
        })
      }
    }

    navigate("/")


  }

  return (
    <FixedFooter>
      <div className="flex items-stretch gap-2 sm:gap-3">
        <QuantityStepper
          variant="outline"
          size="footer"
          min={1}
          max={99}
          value={quantity}
          onIncrement={increaseQuantity}
          onDecrement={decreaseQuantity}
          incrementProps={{ "data-testid": "item-qty-increase" }}
          decrementProps={{ "data-testid": "item-qty-decrease" }}
          valueProps={{ "data-testid": "item-qty" }}
        />
        {currentStep > 0 && (
          <Button
            variant="outline"
            tone="neutral"
            size="footer"
            fullWidth
            onClick={() => setCurrentStep((prev) => prev - 1)}
            data-testid="item-back-button"
            className={isOrderable ? "min-w-0" : "min-w-0 max-sm:w-auto max-sm:shrink-0"}
          >
            Voltar
          </Button>
        )}
        <Button
          align="between"
          size="footer"
          fullWidth
          onClick={handleSubmit}
          disabled={!isOrderable || hasUnfilledMandatoryGroup}
          data-testid="add-to-cart-button"
          className={isOrderable ? "whitespace-nowrap" : "min-w-0"}
        >
          {isOrderable ? (
            <>
              <span>Adicionar</span>
              <Price as="strong" value={itemTotalPrice * quantity} data-testid="add-to-cart-price" />
            </>
          ) : (
            <Text as="span" size={{ base: "base", sm: "lg" }} leading="tight" className="min-w-0">
              Indisponível no momento
            </Text>
          )}
        </Button>
      </div>
    </FixedFooter>
  )
}
